import { Badge, Flex, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import Icon from "../../../../Icon"
import Images from "../../../../Images"
import { statusTextMap, tagColorMap, tagMap } from "../../../../Utils/utils"
import { SOURCE_TYPE, SOURCE_TYPE_NAV } from "./columns"

dayjs.extend(utc)

const { Text } = Typography

const BalanceActivityCard = ({ record, theme }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const statusLabel = statusTextMap[record?.status] || record?.status
  const dateStr = record?.balance_transaction_timestamp
    ? dayjs(record.balance_transaction_timestamp).format("DD MMM, YYYY h:mmA") + " (UTC)"
    : "—"

  const renderSourceId = () => {
    const sourceIdValue = record?.source_id || record?.invoice_id
    if (!sourceIdValue) return "—"
    const isClickable =
      record?.source_type === SOURCE_TYPE.LOOKUP ||
      ([SOURCE_TYPE.CREDIT_REQUEST, SOURCE_TYPE.ALERT].includes(record?.source_type) &&
        ["Charged", "Credited"].includes(record?.subtype) &&
        (record?.is_source_id_linkable || record?.source_type !== SOURCE_TYPE.CREDIT_REQUEST)) ||
      !!record.payment_url
    const onClick = () => {
      if (!isClickable) return
      if (record.payment_url) {
        window.open(record.payment_url, "_blank")
        return
      }
      const buildPath = SOURCE_TYPE_NAV[record?.source_type]
      if (buildPath) navigate(buildPath(record.source_id))
    }
    return (
      <div className="balance-activity-card__copy-row">
        <Text
          onClick={onClick}
          style={isClickable ? { cursor: "pointer", color: "#225AEA" } : undefined}
          className="balance-activity-card__source-id-text"
          title={sourceIdValue}
        >
          {sourceIdValue}
        </Text>
        <button
          type="button"
          className="balance-activity-card__copy-btn"
          onClick={(e) => {
            e.stopPropagation()
            navigator.clipboard.writeText(record.payment_url || sourceIdValue)
          }}
        >
          <Icon type="copy" />
        </button>
      </div>
    )
  }

  const renderAmount = () => {
    const currency = record?.currency || "$"
    const amount = record?.amount
    if (amount == null) return "—"
    const isDebtCreation = amount < 0
    const absValue =
      typeof amount === "string"
        ? amount.startsWith("-")
          ? amount.substring(1)
          : amount
        : Math.abs(amount)
    const displayValue = isDebtCreation ? absValue : amount
    const formatted = `${currency} ${displayValue}`
    if (isDebtCreation) return <Text type="danger">{formatted}</Text>
    if (record?.transaction_type === "Paid Debt") return <Text type="success">{formatted}</Text>
    return <Text style={{ fontWeight: 500 }}>{formatted}</Text>
  }

  const renderBalanceValue = (value) => {
    return value != null ? `$ ${value}` : "—"
  }

  return (
    <div className="balance-activity-card">
      <div className="balance-activity-card__badge">
        <Badge
          status={tagMap[record?.status?.toLowerCase()] || "default"}
          color={tagColorMap[record?.status]}
        />
        <Text style={{ fontSize: 14, fontWeight: 500, letterSpacing: "-0.3px" }}>
          {statusLabel}
        </Text>
      </div>

      <div className="balance-activity-card__row">
        <div className="balance-activity-card__field">
          <span className="balance-activity-card__label">{t("Date")}</span>
          <Text className="balance-activity-card__value balance-activity-card__value--ellipsis">
            {dateStr}
          </Text>
        </div>
        <div className="balance-activity-card__field">
          <span className="balance-activity-card__label">{t("Amount")}</span>
          <span className="balance-activity-card__value">{renderAmount()}</span>
        </div>
      </div>

      <div className="balance-activity-card__row">
        <div className="balance-activity-card__field">
          <span className="balance-activity-card__label">{t("Payment method")}</span>
          {record?.brand ? (
            <Flex align="center" gap={6}>
              <img
                src={Images[`${record.brand.toLowerCase()}${theme === "dark" ? "_dark" : ""}`]}
                width={20}
                height={20}
                alt={record.brand}
              />
              <Text className="balance-activity-card__value">
                {"•••• " + (record?.card_last4 || "")}
              </Text>
            </Flex>
          ) : (
            <Text className="balance-activity-card__value">—</Text>
          )}
        </div>
        <div className="balance-activity-card__field">
          <span className="balance-activity-card__label">{t("Account")}</span>
          <Text className="balance-activity-card__value">{record?.account || "—"}</Text>
        </div>
      </div>

      <div className="balance-activity-card__row">
        <div className="balance-activity-card__field">
          <span className="balance-activity-card__label">{t("Type")}</span>
          <Text className="balance-activity-card__value">
            {record?.subtype ? statusTextMap[record.subtype] || record.subtype : "—"}
          </Text>
        </div>
        <div className="balance-activity-card__field">
          <span className="balance-activity-card__label">{t("Payment source")}</span>
          <Text className="balance-activity-card__value">{record?.source_type || "—"}</Text>
        </div>
      </div>

      <div className="balance-activity-card__row">
        <div className="balance-activity-card__field">
          <span className="balance-activity-card__label">{t("Balance Before")}</span>
          <Text className="balance-activity-card__value">
            {renderBalanceValue(record?.balance_before)}
          </Text>
        </div>
        <div className="balance-activity-card__field">
          <span className="balance-activity-card__label">{t("Balance After")}</span>
          <Text className="balance-activity-card__value">
            {renderBalanceValue(record?.balance_after)}
          </Text>
        </div>
      </div>

      <div className="balance-activity-card__field balance-activity-card__field--full">
        <span className="balance-activity-card__label">{t("Source ID")}</span>
        <div className="balance-activity-card__source-id">{renderSourceId()}</div>
      </div>
    </div>
  )
}

export default BalanceActivityCard
